/**
 * Primary editor is a single {@link HTMLTextAreaElement}, which cannot spellcheck
 * some ranges and skip others. When the buffer embeds large base64 (data URLs,
 * Nulla clips, Max HTML embeds), disable spellcheck for the whole field to avoid
 * false squiggles and heavy native work on non-words.
 */

/** Min length of a base64-ish run after `;base64,` to treat as “binary island”. */
const MIN_DATA_URL_BASE64_RUN = 320;

/** Min length after `npp-html-embed:1hXX;` payload (standard base64). */
const MIN_MAX_HTML_EMBED_BASE64_RUN = 200;

/** Nulla: JSON line after marker, then base64 body — min base64 tail length. */
const MIN_NULLA_VIDEO_BASE64_RUN = 400;

/** Nulla audio: hex payload line length threshold (mic / standalone audio). */
const MIN_NULLA_AUDIO_HEX_RUN = 500;

const RE_DATA_URL_BASE64 = new RegExp(
  `;base64,\\s*[A-Za-z0-9+/=\\r\\n]{${MIN_DATA_URL_BASE64_RUN},}`,
  "i",
);

const RE_MAX_HTML_EMBED_BASE64 = new RegExp(
  `npp-html-embed:1h[0-9a-f]{2};\\s*[A-Za-z0-9+/=\\r\\n]{${MIN_MAX_HTML_EMBED_BASE64_RUN},}`,
  "i",
);

const RE_NULLA_VIDEO_BODY = new RegExp(
  `===NULLA_VIDEO===\\r?\\n[^\\n]+\\r?\\n[A-Za-z0-9+/=\\s\\r\\n]{${MIN_NULLA_VIDEO_BASE64_RUN},}`,
);

const RE_NULLA_AUDIO_BODY = new RegExp(
  `===NULLA_AUDIO===\\r?\\n[^\\n]+\\r?\\n[0-9a-f\\s\\r\\n]{${MIN_NULLA_AUDIO_HEX_RUN},}`,
  "i",
);

/**
 * Returns true when native spellcheck should be off for the whole textarea
 * because of embedded binary-looking regions.
 */
export function tabTextEditorSpellcheckBlockedByBuffer(content: string): boolean {
  if (!content) return false;
  if (RE_DATA_URL_BASE64.test(content)) return true;
  if (RE_MAX_HTML_EMBED_BASE64.test(content)) return true;
  if (RE_NULLA_VIDEO_BODY.test(content)) return true;
  if (RE_NULLA_AUDIO_BODY.test(content)) return true;
  return false;
}
